/**
 * WarehouseProblemCallout — one way to show a warehouse failure (ACT2-1077).
 *
 * The server sends a `WarehouseProblem`: a plain headline, one next step, and support details. This
 * renders the headline and the next step, and puts the vendor's code and request id, Lookout's
 * support code and the driver's own text behind "Show details", with one button that copies them all
 * for a support ticket. The words come from the server; nothing here rewrites them.
 *
 * Lookout's own validation text ("Database is required"), and a RudderStack validator step that fits
 * no kind, are what the admin needs to fix the form, so those details are shown, not hidden.
 */
import type { WarehouseProblem } from '@server/warehouse-failure';
import { ChevronDown, ChevronRight } from 'lucide-react';
import * as React from 'react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';

import { supportText } from './warehouseProblemSupport';

export interface WarehouseProblemCalloutProps {
  problem: WarehouseProblem;
  /** Replaces the server headline when the form knows more, e.g. PR #944's role-specific copy. */
  headline?: React.ReactNode;
}

export function WarehouseProblemCallout({ problem, headline }: WarehouseProblemCalloutProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  // Lookout's own validation text, or RudderStack's validator step text when no kind fits it: in both
  // cases the detail is what the admin acts on, so it is shown.
  const showDetailsInline =
    (problem.kind === 'invalid_config' || problem.hint === 'validator_step') && Boolean(problem.details);
  const support = supportText(problem);
  // Only offer the disclosure when it hides something.
  const hasHidden =
    [problem.supportId, problem.code, problem.vendorRequestId].some(Boolean) ||
    (Boolean(problem.details) && !showDetailsInline);
  const detailsId = React.useId();

  return (
    <Callout variant="error" role="alert" data-problem-kind={problem.kind}>
      <div className="grid gap-1">
        <p className="font-medium">
          {problem.stage ? `${problem.stage}: ` : ''}
          {headline ?? problem.message}
        </p>
        <p>{problem.nextStep}</p>
        {showDetailsInline && <p className="text-foreground/80">{problem.details}</p>}
        {hasHidden && (
          <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="text"
                size="sm"
                className="-ml-1 h-auto justify-start gap-1 px-1 py-0.5 text-current hover:text-current"
                label={open ? 'Hide details' : 'Show details'}
                aria-expanded={open}
                aria-controls={detailsId}
                Icon={open ? <ChevronDown /> : <ChevronRight />}
              />
            </CollapsibleTrigger>
            <CollapsibleContent id={detailsId}>
              <div className="mt-1 grid gap-2 rounded-md bg-background/60 p-2 text-foreground">
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[0.8125rem]">
                  {problem.supportId && (
                    <>
                      <dt className="text-muted-foreground">Support code</dt>
                      <dd className="font-mono break-all">{problem.supportId}</dd>
                    </>
                  )}
                  {problem.code && (
                    <>
                      <dt className="text-muted-foreground">Error code</dt>
                      <dd className="font-mono break-all">{problem.code}</dd>
                    </>
                  )}
                  {problem.vendorRequestId && (
                    <>
                      <dt className="text-muted-foreground">Warehouse request ID</dt>
                      <dd className="font-mono break-all">{problem.vendorRequestId}</dd>
                    </>
                  )}
                  {problem.details && !showDetailsInline && (
                    <>
                      <dt className="text-muted-foreground">Details</dt>
                      <dd className="font-mono break-words whitespace-pre-wrap">{problem.details}</dd>
                    </>
                  )}
                </dl>
                <div>
                  <CopyButton text={support} copyLabel="Details" label="Copy details" copiedFeedback />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </Callout>
  );
}
